import { useCallback, useEffect, useState } from "react";

/**
 * useInsightsPrefs — user-scoped visible-widget preferences for the Insights
 * page. Persisted to localStorage so the choice survives reloads. Falls back
 * gracefully when the storage is blocked (private/iframe).
 *
 * Josh (Feb 9): "Make it so with insight there are heaps of different useful
 * things so they can see but they can also choose what they want to see." The
 * hook exposes a `visible` set + toggle/reset helpers. Widget authors just
 * wrap their block in <WidgetBlock id="…"> and this hook decides whether to
 * render or skip it.
 *
 * `defaults` is the source-of-truth list of widget ids that ship enabled
 * out of the box for a new user. If we add a widget later and want it on by
 * default, add it here — existing users will still see it because we treat
 * missing keys as "on unless the user explicitly toggled it off".
 */
const STORAGE_KEY = "sl_insights_widgets_v1";

export function useInsightsPrefs(defaults) {
  const [off, setOff] = useState(() => loadOffSet());

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...off])); }
    catch { /* storage blocked — session-only preference */ }
  }, [off]);

  const isOn = useCallback((id) => !off.has(id), [off]);
  const setOn = useCallback((id, on) => {
    setOff((prev) => {
      const s = new Set(prev);
      if (on) s.delete(id); else s.add(id);
      return s;
    });
  }, []);
  const toggle = useCallback((id) => {
    setOff((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }, []);
  const enableAll = useCallback(() => setOff(new Set()), []);
  const disableAll = useCallback(() => setOff(new Set(defaults)), [defaults]);
  const reset = useCallback(() => setOff(new Set()), []);

  return { isOn, toggle, setOn, reset, enableAll, disableAll, off };
}

function loadOffSet() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
}
